import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@ozpanel/ui";
import { groupFeed, type FeedGroup, type FeedRow } from "./notificationGroups";

// The two lists every notification view shows, read the same way by the bell and the feed page.
//
// "Open now" is asked for by STATE, not found inside the recent window: a fault that opened 13 days
// ago and is still open sits behind every newer row, so a window of 25 left it out entirely. Measured
// 2026-11-02 on the account console: the bell (window 100) showed one open error, the Notifications
// page (window 25) said "Open now · 0" while the server's own `open` count said 1.
//
// One query key prefix ("me-notifications") so the bell's stream refetch, mark-read and clear refresh
// both views at once.

type ListResponse = { items: FeedRow[] | null; total?: number; unread?: number | null; open?: number | null };

const OPEN_LIMIT = 200; // the server's maximum page

export interface NotificationLists {
  recent: ReturnType<typeof useQuery<ListResponse>>;
  /** Rows in the recent window. */
  items: FeedRow[];
  /** Every open fault in scope, folded across nodes. */
  openGroups: FeedGroup[];
  /** The recent window minus what is open. */
  earlier: FeedGroup[];
  /** The server could count what is open (null/absent: it could not, or predates episodes). */
  openKnown: boolean;
  unread: number | null;
}

export function useNotificationLists(limit: number, opts?: { refetchInterval?: number }): NotificationLists {
  const { client } = useAuth();
  const recent = useQuery({
    queryKey: ["me-notifications", "recent", limit],
    queryFn: () => client.apiFetch<ListResponse>(`/me/notifications?limit=${limit}`),
    refetchInterval: opts?.refetchInterval,
    retry: false,
  });
  const openKnown = recent.data?.open != null;
  const open = useQuery({
    queryKey: ["me-notifications", "open"],
    queryFn: () => client.apiFetch<ListResponse>(`/me/notifications?state=open&limit=${OPEN_LIMIT}`),
    // Only when the server keeps episodes; otherwise there is no "open" to ask for.
    enabled: openKnown,
    refetchInterval: opts?.refetchInterval,
    retry: false,
  });

  const items = React.useMemo(() => recent.data?.items ?? [], [recent.data]);
  const openItems = React.useMemo(() => open.data?.items ?? [], [open.data]);
  const openGroups = React.useMemo(
    () => (openKnown ? groupFeed(openItems) : groupFeed(items).filter((g) => g.state === "open")),
    [openKnown, openItems, items],
  );
  const earlier = React.useMemo(() => groupFeed(items).filter((g) => g.state !== "open"), [items]);

  return {
    recent,
    items,
    openGroups,
    earlier,
    openKnown,
    unread: recent.data?.unread ?? null,
  };
}
